import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { base64urlToUint8Array, uint8ArrayToBase64url } from '../utils/webauthn';
import toast from 'react-hot-toast';

interface PasskeyConfig {
  login: boolean;
  security_settings: boolean;
  delete_account: boolean;
  end_session: boolean;
  end_all_sessions: boolean;
  revoke_device: boolean;
  create_cli_token: boolean;
  access_blocking: boolean;
  new_folder_creation: boolean;
}


/**
 * Hook to handle passkey challenges for protected operations
 * @param operation - The operation type to check passkey requirement for
 * @returns Object with challenge function and loading state
 */
export const usePasskeyChallenge = (operation: keyof PasskeyConfig) => {
  const { user } = useAuth();
  const [isChallenging, setIsChallenging] = useState(false);

  // Load user preferences to check if passkey is required
  const loadUserPreferences = useCallback(async () => {
    try {
      const response = await apiService.getUserPreferences();
      // API returns { preferences: {...} }, extract the preferences object
      const prefs = response?.preferences || response;
      return prefs;
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      return null;
    }
  }, []);

  /**
   * Check if passkey is required for the operation
   */
  const isPasskeyRequired = useCallback(async (): Promise<boolean> => {
    if (!user) {
      console.log('usePasskeyChallenge: No user, passkey not required');
      return false;
    }

    // Always reload preferences to ensure we have the latest data
    const prefs = await loadUserPreferences();
    if (!prefs) {
      console.log('usePasskeyChallenge: No preferences loaded, passkey not required');
      return false;
    }

    const required = prefs.enable_passkey === true && prefs.passkey_config?.[operation] === true;
    console.log(`usePasskeyChallenge: Operation ${operation}, enable_passkey: ${prefs.enable_passkey}, config: ${JSON.stringify(prefs.passkey_config)}, required: ${required}`);
    return required;
  }, [user, operation, loadUserPreferences]);

  /**
   * Perform passkey challenge and return success status
   */
  const performChallenge = useCallback(async (): Promise<boolean> => {
    if (!user) {
      toast.error('User not authenticated');
      return false;
    }

    if (!user.email) {
      toast.error('User email not available');
      return false;
    }

    try {
      setIsChallenging(true);
      toast.loading('Authenticating with passkey...', { id: 'passkey-challenge' });

      // Step 1: Begin authentication - use email instead of user ID
      const beginResponse = await apiService.webauthnBeginAuthentication(undefined, user.email);
      const { options, session_id } = beginResponse;

      // Step 2: Use browser WebAuthn API to get assertion
      const challengeBytes = base64urlToUint8Array(options.publicKey.challenge) as unknown as Uint8Array;
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: challengeBytes.buffer as ArrayBuffer,
          allowCredentials: options.publicKey.allowCredentials?.map((cred: any) => {
            const idBytes = base64urlToUint8Array(cred.id) as unknown as Uint8Array;
            return {
              id: idBytes.buffer as ArrayBuffer,
              type: cred.type,
              transports: cred.transports,
            };
          }),
          timeout: options.publicKey.timeout || 60000,
          userVerification: options.publicKey.userVerification || 'required',
        },
      }) as PublicKeyCredential;

      if (!credential) {
        toast.dismiss('passkey-challenge');
        toast.error('Passkey authentication cancelled');
        return false;
      }

      // Step 3: Convert response to WebAuthn JSON format
      const response = credential.response as AuthenticatorAssertionResponse;
      const assertionResponse = {
        id: credential.id,
        rawId: uint8ArrayToBase64url(new Uint8Array(credential.rawId)),
        response: {
          authenticatorData: uint8ArrayToBase64url(new Uint8Array(response.authenticatorData)),
          clientDataJSON: uint8ArrayToBase64url(new Uint8Array(response.clientDataJSON)),
          signature: uint8ArrayToBase64url(new Uint8Array(response.signature)),
          userHandle: response.userHandle ? uint8ArrayToBase64url(new Uint8Array(response.userHandle)) : null,
        },
        type: credential.type,
      };

      // Step 4: Finish authentication (purpose: "challenge" - don't create a new session)
      await apiService.webauthnFinishAuthentication(session_id, assertionResponse, 'challenge');

      toast.dismiss('passkey-challenge');
      toast.success('Passkey verified');
      return true;
    } catch (error: any) {
      toast.dismiss('passkey-challenge');
      console.error('Passkey challenge error:', error);
      let errorMessage = 'Passkey authentication failed';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Passkey authentication was cancelled or not allowed';
      } else if (error.name === 'InvalidStateError') {
        errorMessage = 'Passkey is invalid or not registered';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Passkeys are not supported in this browser';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return false;
    } finally {
      setIsChallenging(false);
    }
  }, [user]);

  /**
   * Execute an operation with passkey challenge if required
   * @param operationFn - The function to execute after passkey verification (if required)
   */
  const executeWithChallenge = useCallback(async (
    operationFn: () => Promise<void> | void
  ): Promise<boolean> => {
    // Check if passkey is required
    const required = await isPasskeyRequired();
    
    if (required) {
      // Perform passkey challenge
      const verified = await performChallenge();
      if (!verified) {
        return false; // Challenge failed, don't execute operation
      }
    }

    // Execute the operation
    try {
      await operationFn();
      return true;
    } catch (error) {
      console.error('Operation failed:', error);
      throw error;
    }
  }, [isPasskeyRequired, performChallenge]);

  return {
    isPasskeyRequired,
    performChallenge,
    executeWithChallenge,
    isChallenging,
  };
};

